import { useCallback, useEffect, useRef, useState } from 'react';
import * as API from '../../../../api/index.js';
import { useAuthContext } from '../../../../context-state/AuthContext.jsx';
import { useEndUsersContext } from '../../../../context-state/EndUsersContext.jsx';

/**
 * Loads everything the single end-user tabs read from EndUsersContext (end user, user events,
 * seller events, sub accounts, event orders, season tickets). Extracted from SingleEndUserGuard
 * so the WhatsApp contact panel can mount the same tabs for a conversation's end user (#1336).
 * Returns the context entry (may be a previous load while `loading`), plus loading/error/reload.
 */
export function useLoadEndUserFullData(endUserId, { refetch = true } = {}) {
    const { getPrimaryAccount } = useAuthContext();
    const ctx = useEndUsersContext();
    // Read the context through a ref: updateEndUsersFullData deep-copies the map from ITS render's
    // closure, so a memoised stale copy could clobber a concurrent write (e.g. a comment added in a tab).
    const ctxRef = useRef(ctx);
    ctxRef.current = ctx;
    const sellerAccount = getPrimaryAccount();
    const sellerId = sellerAccount?._id;
    // Lazy: a refetch:false cache hit starts (and stays) not-loading, so it never flashes a spinner.
    const [loading, setLoading] = useState(() => !!endUserId && (refetch || !ctx.getEndUserFullData(endUserId)));
    const [error, setError] = useState(null);

    // Only sellerAccount._id / sellerAccount.marketPlace._id are read, both invariant for sellerId,
    // so sellerAccount is intentionally not a dep.
    // Request sequence: only the latest load may touch loading/error (A→B navigation keeps the guard mounted).
    const reqRef = useRef(0);
    const load = useCallback(async () => {
        if (!endUserId || !sellerId) { setLoading(false); return; }
        const reqId = ++reqRef.current;
        setLoading(true); setError(null);
        try {

            const accounts = []
            const events = []
            
            const [endUserResponse, userEventsResponse, sellerEventsResponse, allAccountsResponse, seasonsTicketsResponse] = await Promise.all([
                API.getEndUserById({ endUserId, accountId: sellerAccount._id, marketplaceId: sellerAccount.marketPlace._id }),
                API.getUserEventsByEndUser({ endUserId, accountId: sellerAccount._id }),
                API.getSellerEvents(sellerAccount._id),
                API.getSubAccountsByParentId(sellerAccount._id),
                // The only caller that asks for gate-entry counts (nimi-backend#1312): this load is what the
                // cards render first. EndUserSeasonTicket's refetch-after-edit carries the counts over.
                API.getEndUserSeasonTickets({ endUserId, sellerId: sellerAccount._id, marketplaceId: sellerAccount.marketPlace._id, withVisitCounts: true })
            ])

            events.push(...sellerEventsResponse.data)
            accounts.push(...allAccountsResponse.data)

            const userEventsIds = userEventsResponse.data.map(userEvent => userEvent._id)

            const allAccountsData = accounts.map(account => {
                return {
                    accountId: account._id,
                    account,
                    events: events.filter(event => event.account === account._id)
                }
            })

            // This have to be at the end because we first need to fetch all the userEvents
            const eventOrdersResponse = await API.getEventOrdersOfByUserEvents({ marketplaceId: sellerAccount.marketPlace._id, accountId: sellerAccount._id, userEventsIds })

            // Taken from the ref only now, after the awaits, so the writers close over the latest map.
            const { updateEndUsersFullData, setAllAccountsData } = ctxRef.current;

            setAllAccountsData(allAccountsData)
            
            updateEndUsersFullData(endUserId, accounts, events, userEventsResponse.data, endUserResponse.data, eventOrdersResponse.data, seasonsTicketsResponse.data)

        } catch (e) {
            if (reqId === reqRef.current) setError(e);
        } finally {
            if (reqId === reqRef.current) setLoading(false);
        }
    }, [endUserId, sellerId]);

    // The guard (single end-user page) always refetches on mount, as today. The WhatsApp panel passes
    // refetch:false so switching back to a conversation whose customer is already in the context does
    // not fire the six requests again; `reload` always forces.
    // `refetch` is deliberately not a dep: it is a mount-time policy, and adding it would double-load if a caller flipped it.
    useEffect(() => {
        if (!refetch && ctxRef.current.getEndUserFullData(endUserId)) { setLoading(false); return; }
        load();
    }, [load]);

    return { data: ctx.getEndUserFullData(endUserId), loading, error, reload: load };
}
